import React from "react";
import { Link } from "react-router-dom";
import { FadeUp, Eyebrow } from "@/components/Motion";
import PageHero from "@/components/PageHero";
import { imgUrl } from "@/lib/api";
import { useSiteMedia } from "@/context/SiteMediaContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useSeo } from "@/lib/useSeo";

const ROOM_IMG = ["/gallery/photo4.webp", "/gallery/placeholder.jpg", "/gallery/photo2.webp"];
const ROOM_KEYS = ["cohorts_room_1", "cohorts_room_2", "cohorts_room_3"];

export default function Studio() {
  const { m } = useSiteMedia();
  const { c, media } = useSiteContent();
  useSeo(c("seo_studio_title"), c("seo_studio_desc"));
  const hero = media["cohorts_hero"] || {};

  return (
    <div data-testid="studio-page">
      <PageHero
        eyebrow={c("studio_hero_subtitle", "")}
        title={c("studio_hero_title", "")}
        image={imgUrl(m("cohorts_hero", "/gallery/photo3.webp"))}
        overlay={hero.overlay}
        focal={hero.focal}
      />

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            {c("studio_philosophy_eyebrow") && <Eyebrow>{c("studio_philosophy_eyebrow")}</Eyebrow>}
          </div>
          <FadeUp className="md:col-span-9">
            {c("studio_philosophy_title") && (
              <p className="font-serif text-3xl md:text-4xl font-light leading-[1.3]">
                {c("studio_philosophy_title")}
              </p>
            )}
            {c("studio_philosophy_text") && (
              <p className="text-matte/60 leading-relaxed mt-6 max-w-2xl">
                {c("studio_philosophy_text")}
              </p>
            )}
          </FadeUp>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pb-24 space-y-24 md:space-y-40">
        {[1, 2, 3].map((n, i) => {
          const title = c(`studio_room_${n}_title`);
          if (!title) return null;
          return (
            <FadeUp key={n}>
              <div className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                <div className="overflow-hidden [direction:ltr]">
                  <img src={imgUrl(m(ROOM_KEYS[i], ROOM_IMG[i]))} alt={title} className="w-full h-[50vh] md:h-[68vh] object-cover" />
                </div>
                <div className="[direction:ltr]">
                  <h3 className="font-serif text-4xl md:text-5xl font-light mt-3 mb-6">{title}</h3>
                  <p className="text-matte/60 leading-relaxed max-w-md">{c(`studio_room_${n}_desc`)}</p>
                </div>
              </div>
            </FadeUp>
          );
        })}
      </section>

      {c("studio_cta_label") && (
        <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-28 md:pb-40 text-center">
          <FadeUp>
            <Link
              to={c("studio_cta_link", "/programs")}
              data-testid="studio-programs-btn"
              className="inline-block border border-matte px-9 py-4 text-[12px] uppercase tracking-editorial hover:bg-matte hover:text-ivory transition-colors duration-300"
            >
              {c("studio_cta_label", "Discover Our Programs")}
            </Link>
          </FadeUp>
        </section>
      )}
    </div>
  );
}
